/**
 * Normalize API / Axios / service errors into a safe user-facing string.
 * Never returns "[object Object]" or an empty string.
 */
export function getApiErrorMessage(
  error,
  fallback = 'Something went wrong. Please try again.'
) {
  const safeFallback =
    typeof fallback === 'string' && fallback.trim()
      ? fallback.trim()
      : 'Something went wrong. Please try again.';

  if (error == null) return safeFallback;

  if (typeof error === 'string') {
    const trimmed = error.trim();
    return trimmed || safeFallback;
  }

  // Service-layer wrappers: { success: false, error } or { message }
  if (typeof error === 'object' && error.response == null) {
    if (error.error != null) {
      return getApiErrorMessage(error.error, safeFallback);
    }
    if (typeof error.message === 'string' && error.message.trim()) {
      return sanitizeNetworkMessage(error.message.trim(), safeFallback);
    }
  }

  const data = error?.response?.data;
  if (data == null) {
    if (typeof error?.message === 'string' && error.message.trim()) {
      return sanitizeNetworkMessage(error.message.trim(), safeFallback);
    }
    return safeFallback;
  }

  if (typeof data === 'string' && data.trim()) {
    // Avoid dumping HTML error pages into toasts
    if (/^\s*</.test(data)) return safeFallback;
    return data.trim();
  }

  const fromMessage = coerceMessageValue(data.message);
  if (fromMessage) return fromMessage;

  const fromError = coerceMessageValue(data.error);
  if (fromError) return fromError;

  if (data.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors)
      .flat()
      .map((m) => coerceMessageValue(m))
      .find(Boolean);
    if (first) return first;
  }

  // Laravel Validator::errors() often returned as root-level field bags:
  // { email: ['…'], password: ['…'] } without a `message` / `errors` wrapper.
  if (typeof data === 'object' && !Array.isArray(data)) {
    const fieldMessages = Object.entries(data)
      .filter(([key]) => !['success', 'token', 'user', 'role', 'data'].includes(key))
      .map(([, value]) => coerceMessageValue(value))
      .find(Boolean);
    if (fieldMessages) return fieldMessages;
  }

  return safeFallback;
}

function coerceMessageValue(value) {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const inner = coerceMessageValue(item);
      if (inner) return inner;
    }
    return null;
  }
  if (typeof value === 'object') {
    // e.g. { email: ['…'] } nested under message
    const first = Object.values(value)
      .flat()
      .map((m) => coerceMessageValue(m))
      .find(Boolean);
    return first || null;
  }
  return null;
}

function sanitizeNetworkMessage(message, fallback) {
  if (message === 'Network Error') {
    return 'Network error. Please check your connection and try again.';
  }
  if (/^timeout of \d+ms exceeded$/i.test(message)) {
    return 'The request timed out. Please try again.';
  }
  if (/^Request failed with status code \d+$/i.test(message)) {
    return fallback;
  }
  return message;
}

/** Coerce any toast payload into a non-empty string. */
export function toUserMessage(value, fallback = 'Something went wrong. Please try again.') {
  if (value == null || value === '') {
    return getApiErrorMessage(null, fallback);
  }
  if (typeof value === 'string') {
    return value.trim() || getApiErrorMessage(null, fallback);
  }
  return getApiErrorMessage(value, fallback);
}
